import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { domainWithHttp } from "../../../config/onboarding/onBoarding";

interface ExportPDFProps {
    params: any;
    index: any;
    alluraFont: string;
    candidateData: any;
    fileNameModifide: (file: any) => string;
}


//
// ---------- Helper Functions ----------
//

const initPDF = (alluraFont: string) => {
    const doc = new jsPDF();
    doc.addFileToVFS("Allura-Regular.ttf", alluraFont);
    doc.addFont("Allura-Regular.ttf", "Allura", "normal");
    return doc;
};

const getHeaderText = () => {
    const d = new Date();
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `Document created on : ${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const formatDate = (date: Date) => {
    const pad = (n: number) => n.toString().padStart(2, "0");
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
};

// Strips HTML tags and decodes entities, returning plain text safe for PDF
const stripHtml = (value: any): any => {
    if (typeof value !== "string") { return value; }
    const parser = new DOMParser();
    const parsed = parser.parseFromString(value, "text/html");
    return parsed.body.textContent ?? "";
};

// --- Handle subforms (nested data) ---
const handleSubForm = (
    element: any,
    tableBody: any[],
    baseUrl: string,
    fileNameModifide: (file: any) => string
) => {
    element.subForm.forEach((item: any) => {
        if (item.type === "text") {
            tableBody.push([item.pdfLabel, stripHtml(item.value)]);
        } else if (item.type === "date") {
            tableBody.push([item.pdfLabel, item.value ? formatDate(item.value) : ""]);
        } else {
            const fileName: any = [];
            if (Array.isArray(item.value)) {
                item.value.forEach((ress: any) => {
                    const result = fileNameModifide(ress);
                    fileName.push({ name: result, url: `${baseUrl}${encodeURIComponent(ress.file.url)}` });
                });
                tableBody.push([item.pdfLabel, { items: fileName, type: "file-url" }]);
            } else {
                tableBody.push([item.pdfLabel, stripHtml(item.value)]);
            }
        }
    });
};

// --- Handle file fields ---
const handleFileField = (
    element: any,
    tableBody: any[],
    baseUrl: string,
    fileNameModifide: (file: any) => string
) => {
    const fileName: any[] = [];
    if (element?.value.length > 0) {
        element.value.forEach((tt: any) => {
            const result = fileNameModifide(tt);
            fileName.push({
                name: result,
                url: `${baseUrl}${encodeURIComponent(tt.file.url)}`,
            });
        });
        tableBody.push([element.pdfLabel, { items: fileName, type: "file-url" }]);
    } else {
        tableBody.push([element.pdfLabel, ""]);
    }
};

const truncateToFit = (doc: any, name: string, maxWidth: number): string => {
    if (doc.getTextWidth(name) <= maxWidth) { return name; }
    let truncated = name;
    while (truncated.length > 0 && doc.getTextWidth(truncated + "...") > maxWidth) {
        truncated = truncated.slice(0, -1);
    }
    return truncated + "...";
};

// Extracts anchor links from an HTML string
const extractLinksFromHtml = (html: string): { name: string; url: string }[] => {
    const links: { name: string; url: string }[] = [];
    const parser = new DOMParser();
    const parsed = parser.parseFromString(html, "text/html");
    parsed.querySelectorAll("a").forEach((a) => {
        const url = a.getAttribute("href")?.trim() || "";
        const name = a.textContent?.trim() || url;
        if (url) {links.push({ name, url });}
    });
    return links;
};

// Handles different field types and pushes formatted rows to tableBody
const handleElementParsing = (
    element: any,
    tableBody: any[],
    baseUrl: string,
    fileNameModifide: (file: any) => string
) => {
    if (element.type === "date") {
        if (element.value) {
            const date = formatDate(element.value);
            tableBody.push([element.pdfLabel, date]);
        }
    } else if (element.type === "html" && element.value === "" && element?.lableClass !== "d-none") {
        tableBody.push([{ content: element.label, styles: { fontStyle: "bold" } }, ""]);
    } else if (element.type === "html" && element.value) {
        const links = extractLinksFromHtml(element.value);
        if (links.length > 0) {
            tableBody.push([element.label || "", { items: links, type: "file-url" }]);
        }
    } else if (element?.isSignature === true) {
        tableBody.push([element.pdfLabel, { item: element.value, type: "signature" }]);
    } else if (element.type === "file") {
        handleFileField(element, tableBody, baseUrl, fileNameModifide);
    } else if (element.type === "checkbox-group" || element.type === "checkbox") {
        tableBody.push([element.label ? element.pdfLabel : element.values[0].label, stripHtml(element.value)]);
    } else if (element.type === "multiSelect") {
        const multiSelectValue = element.value.map((item: any) => stripHtml(item.value)).join(", ");
        tableBody.push([element.label ? element.pdfLabel : element.values[0].label, multiSelectValue]);
    } else if (["subForm-1", "subForm-2", "subForm-3"].includes(element.name)) {
        handleSubForm(element, tableBody, baseUrl, fileNameModifide);
    } else if (element.type === "text" && element.subtype === "make-bold") {
        tableBody.push([{ items: [{ name: element.pdfLabel, url: "" }], type: "make-bold" }, ""]);
    } else if (element.type !== "iframe" && element.type !== "hr" && element.type !== "html") {
        tableBody.push([element.pdfLabel, stripHtml(element.value)]);
    }
};


// --- Render the table and style ---
const renderAutoTable = (doc: any, tableBody: any[], tableStyles: any, headerText: string) => {
    autoTable(doc, {
        head: [["Label", "Value"]],
        body: tableBody,
        headStyles: tableStyles as any,
        columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 100 } },

        didParseCell: (data: any) => {
            if (data.section === "body" && data.column.index === 1) {
                const signURLData = data.cell.raw;
                if (signURLData?.type === "signature") {
                    data.cell.styles.font = "Allura";
                    data.cell.text = [signURLData?.item || ""];
                } else {
                    data.cell.styles.font = "helvetica";
                }
                if (typeof data.cell.raw === "object" && data.cell.raw?.type === "file-url") {
                    const n = data.cell.raw.items.length;
                    if (n > 1) {
                        const height = 5;
                        data.cell.styles.minCellHeight = height * n + 2;
                        data.row.height = height * n + 2;
                    }
                    data.cell.text = [""];
                }
            } else if (data.section === "body" && data.column.index === 0) {
                if (typeof data.cell.raw === "object" && data.cell.raw?.type === "make-bold") {
                    data.cell.styles.fontStyle = "bold";
                    data.cell.text = [data.cell.raw.items[0].name];
                }
            }
        },

        didDrawCell: (data: any) => {
            if (data.section === "body" && data.column.index === 1) {
                if (typeof data.cell.raw === "object" && data.cell.raw?.type === "file-url") {
                    const maxWidth = data.cell.width - 4;
                    let yPos = 5;
                    doc.setTextColor("#0563C1");
                    data.cell.raw.items.forEach((itm: any) => {
                        const x = data.cell.x + 1;
                        const y = data.cell.y + yPos;
                        const displayName = truncateToFit(doc, itm.name, maxWidth);
                        doc.textWithLink(displayName, x, y, { url: itm.url });
                        const textWidth = doc.getTextWidth(displayName);
                        doc.line(x, y + 0.5, x + textWidth, y + 0.5);
                        yPos += 5;
                    });
                    doc.setTextColor(0);
                }
            }
        },

        didDrawPage: (data: any) => {
            doc.setFontSize(8);
            doc.text(headerText, data.settings.margin.left, 10);
        },
    });
};


/**
 * Generates PDF from form data using jsPDF and autoTable
 * (Refactored for readability, identical behavior)
 */
export const exportPDF = async ({
    params,
    index,
    alluraFont,
    candidateData,
    fileNameModifide,
}: ExportPDFProps) => {
    const doc = initPDF(alluraFont);

    const pdfTable = params.filter((ress: any) => ress.active)[0];
    const baseUrl = `${process.env.REACT_APP_API_BASE_URL}download?domainWithHttp=${encodeURIComponent(domainWithHttp)}&FileUrl=`;
    const tableBody: any[] = [];
    const headerText = getHeaderText();
    let iFrameText = "";

    // Insert main label
    tableBody.push([{ content: pdfTable.label, styles: { fontStyle: "bold" } }, ""]);

    pdfTable?.fields?.forEach((element: any) => {
        handleElementParsing(element, tableBody, baseUrl, fileNameModifide);
        if (element.type === "iframe" && element.iframe_url !== "") {
            iFrameText = element.iframe_url;
        }
    });

    const tableStyles = {
        fillColor: candidateData?.clientDetails?.brandColor,
        fontStyle: "bold",
        fontSize: 10,
    };

    renderAutoTable(doc, tableBody, tableStyles, headerText);

    // Add iframe link
    if (iFrameText !== "") {
        const displayText = pdfTable?.label;
        const finalY = (doc as any).lastAutoTable.finalY + 15;
        doc.setTextColor("#0563C1");
        doc.textWithLink(`View Info : ${displayText}`, 15, finalY, {
            url: iFrameText,
            underline: true,
        });
    }

    const pdfDataUri = doc.output("datauristring");
    return pdfDataUri;
};

export default exportPDF;